import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Checkbox,
  Tooltip,
  FormControl,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNotification } from "context/NotificationContext";
import styles from "./styles.module.scss";

const maxPromptLength = 1500;

const validationSchema = Yup.object({
  requirements: Yup.string().max(maxPromptLength),
  expectedTestCases: Yup.string().max(maxPromptLength),
});

const GenerateTestSettingsModal = ({ open, setOpen, onRun }) => {
  const showNotification = useNotification();
  const [closePopups, setClosePopups] = useState(true);
  const [submitWithContext, setSubmitWithContext] = useState(true);

  const handleClose = () => {
    setOpen(false);
    setClosePopups(true);
    setSubmitWithContext(true);
  };

  const onError = () => {
    showNotification(
      "Error! Something went wrong, please try again later.",
      "error",
      "",
      null,
    );
  };

  const handleRun = async (values, includeContext, setSubmitting) => {
    try {
      setSubmitting(true);
      await onRun({
        requirements: includeContext ? values?.requirements?.trim() || "" : "",
        expectedTestCases: includeContext
          ? values?.expectedTestCases?.trim() || ""
          : "",
        closePopups,
      });
    } catch (error) {
      onError();
    } finally {
      setSubmitting(false);
      handleClose();
    }
  };

  return (
    <Dialog
      className={styles.content}
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-paper": {
          width: "1000px",
          maxWidth: "1000px",
        },
      }}
    >
      <DialogTitle className={styles.title}>Generate Test Cases</DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          requirements: "",
          expectedTestCases: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) =>
          handleRun(values, submitWithContext, setSubmitting)
        }
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          isSubmitting,
          submitForm,
        }) => (
          <Form>
            <DialogContent className={styles.formContent}>
              <Typography className={styles.description}>
                Treegress will automatically discover and generate your test
                cases. You can proceed with the default settings, or provide
                additional requirements to guide the test generation process.
              </Typography>
              <Box className={styles.closePopupsRow}>
                <Checkbox
                  checked={closePopups}
                  onChange={(event) => setClosePopups(event.target.checked)}
                />
                <Typography className={styles.closePopupsLabel}>
                  Close pop ups
                </Typography>
                <Tooltip
                  title="Treegress automatically closes pop-ups that may block tests, but this adds extra time. If the page has no pop-ups, disable this step to speed up execution."
                  arrow
                >
                  <InfoOutlinedIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>

              <FormControl fullWidth margin="dense">
                <Box className={styles.textInputWrapper}>
                  <TextField
                    sx={{
                      "& .MuiInputBase-root": {
                        paddingBottom: "32px",
                      },
                    }}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={12}
                    label="Requirements"
                    placeholder='Add user stories or requirements. Describe how this functionality is supposed to work. Example: User can submit a "Contact Us" form with name, email, and message, then sees a success message.'
                    name="requirements"
                    margin="dense"
                    value={values.requirements}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.requirements)}
                    helperText={
                      <>
                        {errors.requirements}
                        <span className={styles.descriptionCounter}>
                          {`${values?.requirements?.length || 0} / ${maxPromptLength}`}
                        </span>
                      </>
                    }
                  />
                </Box>
              </FormControl>

              <FormControl fullWidth margin="dense">
                <Box className={styles.textInputWrapper}>
                  <TextField
                    sx={{
                      "& .MuiInputBase-root": {
                        paddingBottom: "32px",
                      },
                    }}
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={12}
                    label="Expected test cases"
                    placeholder="Describe what you want the test cases to cover and verify. Example: Check that form can't be submitted with empty fields, invalid emails are rejected, success message appears after valid submit."
                    name="expectedTestCases"
                    margin="dense"
                    value={values.expectedTestCases}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.expectedTestCases)}
                    helperText={
                      <>
                        {errors.expectedTestCases}
                        <span className={styles.descriptionCounter}>
                          {`${values?.expectedTestCases?.length || 0} / ${maxPromptLength}`}
                        </span>
                      </>
                    }
                  />
                </Box>
              </FormControl>
            </DialogContent>

            <DialogActions className={styles.actionWrapper}>
              <Button
                color="secondary"
                className={styles.button}
                onClick={handleClose}
                type="button"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                color="secondary"
                className={styles.button}
                disabled={isSubmitting}
                type="button"
                variant="outlined"
                onClick={() => {
                  setSubmitWithContext(false);
                  submitForm();
                }}
              >
                {isSubmitting ? "Generating..." : "GENERATE WITHOUT CONTEXT"}
              </Button>
              <Button
                color="secondary"
                className={styles.button}
                disabled={
                  isSubmitting ||
                  (!values?.requirements && !values?.expectedTestCases) ||
                  errors.requirements ||
                  errors.expectedTestCases
                }
                type="button"
                variant="contained"
                onClick={() => {
                  setSubmitWithContext(true);
                  submitForm();
                }}
              >
                {isSubmitting ? "Generating..." : "GENERATE WITH CONTEXT"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default GenerateTestSettingsModal;
